import { ref, watch, type Ref } from 'vue';
import { defineStore } from 'pinia';
import api from '@/api';
import type { RailTrain } from '@/@types/rail';
import { isAppVisible } from '@/libs/app-visibility';
import { isRailLinkedStop, railLinkedStopIds } from '@/libs/rail-helper';
import {
  isMockTagsEnabled,
  mockRailArrivals,
  mockRailDepartures,
  mockRailStationLinks,
  onMockTagsReady,
} from '@/libs/dev/mock-tags';
import { useGtfsStore } from './gtfs';

// Same cadence as the trip updates: the boards are realtime data of the same kind.
export const RAIL_BOARD_REFRESH_INTERVAL = 60000;
export const RAIL_BOARD_REFRESH_IF_ERROR_INTERVAL = 10000;

export type RailBoardState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * SNCF data of the network (feature `rail_data`): which stops are attached to a
 * station, and the arrival and departure boards of the stop currently displayed.
 */
export const useRailStore = defineStore('rail', () => {
  const gtfsStore = useGtfsStore();
  const mocking = import.meta.env.DEV && isMockTagsEnabled();

  // #region station links, loaded once per group
  const linkedStopIds = ref<Set<string>>(new Set());
  const linksLoaded = ref<boolean>(false);

  // The links of a group left in the meantime must not land on the one displayed.
  let linksRun = 0;
  let linksGroupId: string | undefined;

  /**
   * A group without the feature, or a failing call, behaves as "no attached stop":
   * the stop sheet stays as it is, silently. A failure still reaches Datadog as a
   * warning; the `403 LOCKED_FEATURE` of a group without `rail_data` is the expected
   * answer for most networks and must not raise one on every launch.
   */
  async function loadStationLinks(groupId: string) {
    const run = (linksRun += 1);
    linksGroupId = groupId;
    linksLoaded.value = false;
    linkedStopIds.value = new Set();
    try {
      const links = await api.getRailStationLinks(groupId);
      if (run !== linksRun) return;
      linkedStopIds.value = railLinkedStopIds(mocking ? [...mockRailStationLinks(), ...links] : links);
    } catch (e) {
      if (run !== linksRun) return;
      // A group without the feature still gets the mocked board in mock mode
      if (mocking) linkedStopIds.value = railLinkedStopIds(mockRailStationLinks());
      if (!isLockedFeature(e)) {
        console.warn('Rail station links unavailable, the stop sheets show no train line:', e);
      }
    } finally {
      if (run === linksRun) linksLoaded.value = true;
    }
  }

  // api-helper throws `<status>: <text> when trying to get <url>` on a completed request
  function isLockedFeature(error: unknown): boolean {
    return error instanceof Error && error.message.startsWith('403:');
  }

  /** False until the links are loaded, so the train line never flashes on a stop sheet. */
  function isStopLinked(stopId: string): boolean {
    return linksLoaded.value && isRailLinkedStop(stopId, linkedStopIds.value, gtfsStore.stations);
  }
  // #endregion

  // #region arrival and departure boards of one stop, polled while its screen is displayed
  const arrivalBoard = railBoard('arrivals', api.getRailArrivals, mockRailArrivals);
  const departureBoard = railBoard('departures', api.getRailDepartures, mockRailDepartures);

  /** Both boards poll on their own: one failing leaves the other displayed and refreshed. */
  function startBoardsPolling(groupId: string, stopId: string) {
    arrivalBoard.start(groupId, stopId);
    departureBoard.start(groupId, stopId);
  }

  function stopBoardsPolling() {
    arrivalBoard.stop();
    departureBoard.stop();
  }

  function railBoard<T extends RailTrain>(
    label: string,
    fetchBoard: (groupId: string, stopId: string) => Promise<Array<T>>,
    mockBoard: (stopId: string) => Array<T>,
  ) {
    const trains = ref<Array<T>>([]) as Ref<Array<T>>;
    const state = ref<RailBoardState>('idle');

    let polled: { groupId: string; stopId: string } | undefined;
    let pollTimeout: NodeJS.Timeout | undefined;
    // A response of a previous board (other stop, or stopped polling) must not land in this one.
    let pollRun = 0;

    function start(groupId: string, stopId: string) {
      stop();
      polled = { groupId, stopId };
      trains.value = [];
      state.value = 'loading';
      refresh();
    }

    function stop() {
      if (pollTimeout) clearTimeout(pollTimeout);
      pollTimeout = undefined;
      polled = undefined;
      pollRun += 1;
    }

    /** Reloads now instead of waiting for the degraded-mode retry. */
    function retry() {
      if (!polled) return;
      if (pollTimeout) clearTimeout(pollTimeout);
      state.value = 'loading';
      refresh();
    }

    async function refresh() {
      if (!polled || !isAppVisible.value) return;
      const run = (pollRun += 1);
      const { groupId, stopId } = polled;
      let nextIn = RAIL_BOARD_REFRESH_INTERVAL;
      try {
        const board = await fetchBoard(groupId, stopId).catch(e => {
          // The `rail_data` feature is locked on most dev groups: mock mode boards them anyway
          if (!mocking) throw e;
          return [];
        });
        if (run !== pollRun) return;
        trains.value = mocking ? [...mockBoard(stopId), ...board] : board;
        state.value = 'ready';
      } catch (e) {
        if (run !== pollRun) return;
        // The last board stays on screen: offline, the network banner says why.
        state.value = 'error';
        nextIn = RAIL_BOARD_REFRESH_IF_ERROR_INTERVAL;
        console.warn(`Error while fetching rail ${label}, retrying in 10s:`, e);
      }
      pollTimeout = setTimeout(refresh, nextIn);
    }

    // Hidden, the board would be refreshed for nobody: shown again, it reloads at once.
    watch(isAppVisible, visible => {
      if (!polled) return;
      if (pollTimeout) clearTimeout(pollTimeout);
      pollTimeout = undefined;
      pollRun += 1;
      if (visible) refresh();
    });

    return { trains, state, start, stop, retry };
  }
  // #endregion

  // The links are fetched before the GTFS the mocked stops are taken from
  if (mocking) {
    onMockTagsReady(() => {
      if (linksGroupId) loadStationLinks(linksGroupId);
    });
  }

  return {
    linkedStopIds,
    linksLoaded,
    loadStationLinks,
    isStopLinked,
    arrivals: arrivalBoard.trains,
    arrivalsState: arrivalBoard.state,
    retryArrivals: arrivalBoard.retry,
    departures: departureBoard.trains,
    departuresState: departureBoard.state,
    retryDepartures: departureBoard.retry,
    startBoardsPolling,
    stopBoardsPolling,
  };
});
